// Shared timing/easing constants + helpers for the dashboard's on-load reveal
// animation system. Keeping these centralized means every card, chart and
// counter animates on the same clock instead of drifting out of sync.

// A soft "ease-out" curve (decelerates quickly, settles gently) used for
// every entrance animation across the dashboard.
export const EASE_OUT = [0.16, 1, 0.3, 1];

// Delay between each staggered card's entrance, in seconds (60-80ms range).
export const STAGGER_STEP = 0.07;

// Duration of a single card's fade/slide-up entrance, in seconds.
export const CARD_DURATION = 0.5;

// Duration of the count-up number animation, in milliseconds.
export const COUNT_UP_DURATION = 1200;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
export { easeOutCubic };

// Formats a number with optional fixed decimals + thousands separators,
// e.g. formatNumber(1745.2, 0, true) -> "1,745"
export function formatNumber(value, decimals = 0, withCommas = true) {
    const fixed = Number(value || 0).toFixed(decimals);
    if (!withCommas) return fixed;
    const [intPart, decPart] = fixed.split('.');
    const withSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart ? `${withSeparators}.${decPart}` : withSeparators;
}

// Builds an SVG path "d" string (M/L commands) from an array of {x, y}
// points — used to turn chart polylines into <motion.path> elements so we
// can animate them drawing in via `pathLength`.
export function buildLinePath(points) {
    if (!points.length) return '';
    return points.reduce((d, point, index) => (
        d + `${index === 0 ? 'M' : 'L'}${point.x},${point.y} `
    ), '').trim();
}

// Builds a smooth cubic-bezier SVG path from an array of {x, y} points.
// Uses the catmull-rom approach: each segment's control points are derived
// from neighbouring points so the curve flows through all data points
// without sharp corners — producing the wave-like shape shown in the snapshot.
export function buildSmoothLinePath(points) {
    if (!points || points.length < 2) return buildLinePath(points);
    const tension = 0.35; // 0 = linear, ~0.4 = smooth wave
    let d = `M${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(i - 1, 0)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(i + 2, points.length - 1)];
        const cp1x = p1.x + (p2.x - p0.x) * tension;
        const cp1y = p1.y + (p2.y - p0.y) * tension;
        const cp2x = p2.x - (p3.x - p1.x) * tension;
        const cp2y = p2.y - (p3.y - p1.y) * tension;
        d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }
    return d;
}

// Builds a closed area path using smooth cubic bezier curves for a gradient
// fill beneath a line chart.
export function buildSmoothAreaPath(points, baselineY = 100) {
    if (!points.length) return '';
    const line = buildSmoothLinePath(points);
    const first = points[0];
    const last = points[points.length - 1];
    return `${line} L${last.x},${baselineY} L${first.x},${baselineY} Z`;
}

// Builds a closed area path (line points + baseline) for a gradient fill
// beneath a line chart.
export function buildAreaPath(points, baselineY = 100) {
    if (!points.length) return '';
    const line = buildLinePath(points);
    const first = points[0];
    const last = points[points.length - 1];
    return `${line} L${last.x},${baselineY} L${first.x},${baselineY} Z`;
}
